/**
 * サイト選択ページ (Phase 3 — Figma #02 準拠).
 *
 * admin-api の GET /me/sites を叩き、所属サイト一覧をカードグリッドで表示。
 * Figma: 3列グリッド、カード + 新規追加ボタン。
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { type FormEvent, useEffect, useState } from 'react';
import { PlanLimitBannerConnected } from '../components/PlanLimitBannerConnected.js';
import {
  authLogout,
  createSandbox,
  createSite,
  fetchMySites,
  type MySiteRow,
} from '../lib/api-client.js';
import { formatErrorMessage } from '../lib/error-messages.js';

const LAST_SITE_ID_KEY = 'petacms_last_site_id';

const STATUS_LABELS: Record<MySiteRow['status'], string> = {
  active: '稼働中',
  trialing: 'トライアル',
  suspended: '停止中',
  expired: '期限切れ',
  sandbox: 'サンドボックス',
};

const STATUS_COLORS: Record<MySiteRow['status'], string> = {
  active: 'bg-[rgba(16,185,129,0.12)] text-[#065f46]',
  trialing: 'bg-[rgba(59,130,246,0.12)] text-[#1e40af]',
  suspended: 'bg-[rgba(239,68,68,0.12)] text-[#991b1b]',
  expired: 'bg-[rgba(112,109,101,0.12)] text-[#706d65]',
  sandbox: 'bg-[rgba(245,158,11,0.12)] text-[#92400e]',
};

export function SitesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    document.title = 'サイト選択 - PetaCMS';
  }, []);

  const {
    data: sites,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['me', 'sites'],
    queryFn: fetchMySites,
  });

  const handleSelect = (siteId: string) => {
    try {
      localStorage.setItem(LAST_SITE_ID_KEY, siteId);
    } catch {
      // ignore
    }
    navigate({ to: '/sites/$siteId/dashboard', params: { siteId } });
  };

  const handleLogout = async () => {
    await authLogout();
    navigate({ to: '/login' });
  };

  const createMutation = useMutation({
    mutationFn: createSite,
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['me', 'sites'] });
      handleSelect(created.id);
    },
  });

  const sandboxMutation = useMutation({
    mutationFn: createSandbox,
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['me', 'sites'] });
      handleSelect(created.id);
    },
  });

  const hasSandbox = !!sites?.some((s) => s.status === 'sandbox');
  const hasAnySite = !!sites && sites.length > 0;

  const handleCreateSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    createMutation.mutate({
      name: String(form.get('name') ?? ''),
      originHost: String(form.get('originHost') ?? ''),
      planId: 'free',
    });
  };

  return (
    <div className="flex min-h-screen flex-col bg-[#f8f7f6]">
      {/* Top bar */}
      <header className="flex h-[52px] shrink-0 items-center justify-between border-b border-[#edebe8] bg-white px-7">
        <Link to="/sites" className="text-[19px] font-bold text-[#23221e]">
          PetaCMS
        </Link>
        <div className="flex items-center gap-4">
          <Link to="/account" className="text-[13px] text-[#706d65] hover:text-[#23221e]">
            アカウント設定
          </Link>
          <button
            type="button"
            onClick={handleLogout}
            className="text-[13px] text-[#706d65] hover:text-[#ef4444]"
          >
            ログアウト
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 p-7">
        <h1 className="text-[16px] font-semibold text-[#23221e]">ダッシュボード</h1>

        {/* プラン上限バナー: 所有サイト数が 80% / 100% に達したら表示。
            usage API は siteId 単位なので最初のサイトを参照用に使う (planId はユーザー横断で同じ) */}
        {sites?.[0] && (
          <div className="mt-4">
            <PlanLimitBannerConnected siteId={sites[0].id} resource="sites" />
          </div>
        )}

        {error && (
          <div className="mt-4 rounded-[6px] border-l-4 border-[#ef4444] bg-[#fef2f2] p-4 text-sm text-[#991b1b]">
            読み込みエラー: {formatErrorMessage(error, '読み込みエラーが発生しました')}
          </div>
        )}

        {isLoading && <div className="mt-6 text-[14px] text-[#706d65]">読み込み中...</div>}

        {/* はじめての場合: オンボーディングカード */}
        {!isLoading && !hasAnySite && !showCreate && (
          <div className="mt-6 rounded-[12px] border border-[#edebe8] bg-white p-7">
            <h2 className="text-[18px] font-bold text-[#23221e]">まず試しに使ってみましょう</h2>
            <p className="mt-2 text-[14px] text-[#706d65]">
              登録不要のサンプル記事・スニペット入りのサンドボックスサイトで、PetaCMS の全機能を 7
              日間試せます。
            </p>
            <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
              <button
                type="button"
                onClick={() => sandboxMutation.mutate()}
                disabled={sandboxMutation.isPending}
                className="rounded-[8px] border-2 border-[#3b82f6] bg-[#eff6ff] p-5 text-left transition hover:bg-[#dbeafe] disabled:opacity-50"
              >
                <div className="text-[15px] font-semibold text-[#1e40af]">
                  {sandboxMutation.isPending ? '作成中...' : '🎁 サンドボックスで試す'}
                </div>
                <p className="mt-2 text-[13px] text-[#706d65]">
                  サンプル記事 5 本 + スニペット 3 個が入った仮想サイトを 1 秒で作成。 記事 20
                  件・画像 10 枚まで、7 日で自動削除されます。
                </p>
              </button>
              <button
                type="button"
                onClick={() => setShowCreate(true)}
                className="rounded-[8px] border border-[#d6d3d0] bg-white p-5 text-left transition hover:bg-[#f8f7f6]"
              >
                <div className="text-[15px] font-semibold text-[#23221e]">
                  🏢 自社サイトで本番運用
                </div>
                <p className="mt-2 text-[13px] text-[#706d65]">
                  既存の Web サイトに埋め込みコードを設置して PetaCMS を接続。 14
                  日無料トライアルから開始。
                </p>
              </button>
            </div>
            {sandboxMutation.error && (
              <div className="mt-4 rounded-[6px] border-l-4 border-[#ef4444] bg-[#fef2f2] p-3 text-sm text-[#991b1b]">
                {formatErrorMessage(sandboxMutation.error, 'サンドボックス作成に失敗しました')}
              </div>
            )}
          </div>
        )}

        {/* Site cards grid */}
        {!isLoading && sites && sites.length > 0 && (
          <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {sites.map((site) => (
              <div
                key={site.id}
                className="rounded-[8px] border border-[#edebe8] bg-white p-5 transition hover:shadow-[0_2px_8px_rgba(0,0,0,0.06)]"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-[16px] font-semibold text-[#23221e]">{site.name}</h2>
                    <p className="mt-1 text-[13px] text-[#c1bdb7]">
                      {site.customHost ?? site.originHost}
                    </p>
                  </div>
                  <span
                    className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_COLORS[site.status]}`}
                  >
                    {STATUS_LABELS[site.status]}
                  </span>
                </div>
                <div className="mt-5 flex items-end justify-between">
                  <div className="text-[13px] text-[#706d65]">
                    <span className="mr-3 rounded-[4px] bg-[#f2f1f0] px-1.5 py-0.5 text-[11px] text-[#706d65]">
                      {site.role}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleSelect(site.id)}
                    className="rounded-[6px] bg-[#eff6ff] px-5 py-[7px] text-[13px] font-medium text-[#3b82f6] transition hover:bg-[#dbeafe]"
                  >
                    管理
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create site */}
        <div className="mt-5 flex flex-wrap items-center gap-3">
          {!showCreate && hasAnySite && !hasSandbox && (
            <button
              type="button"
              onClick={() => sandboxMutation.mutate()}
              disabled={sandboxMutation.isPending}
              className="rounded-[6px] border border-[#3b82f6] bg-white px-5 py-2.5 text-[14px] font-medium text-[#3b82f6] transition hover:bg-[#eff6ff] disabled:opacity-50"
            >
              {sandboxMutation.isPending ? '作成中...' : '🎁 サンドボックスを試す'}
            </button>
          )}
          {!showCreate ? (
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="rounded-[6px] bg-[#3b82f6] px-5 py-2.5 text-[14px] font-medium text-white transition hover:bg-[#1d4ed8]"
            >
              + 新しいサイトを追加
            </button>
          ) : (
            <form
              onSubmit={handleCreateSubmit}
              className="max-w-md space-y-4 rounded-[8px] border border-[#edebe8] bg-white p-5"
            >
              <div>
                <label
                  htmlFor="create-name"
                  className="mb-2 block text-[13px] font-medium text-[#23221e]"
                >
                  サイト名
                </label>
                <input
                  id="create-name"
                  name="name"
                  type="text"
                  required
                  maxLength={80}
                  placeholder="マイブログ"
                  className="h-[44px] w-full rounded-[6px] border border-[#d6d3d0] bg-white px-[14px] text-[14px] text-[#23221e] placeholder:text-[#c1bdb7] focus:border-[#3b82f6] focus:outline-none focus:ring-2 focus:ring-[#3b82f6]/25"
                />
              </div>
              <div>
                <label
                  htmlFor="create-host"
                  className="mb-2 block text-[13px] font-medium text-[#23221e]"
                >
                  Origin Host
                </label>
                <input
                  id="create-host"
                  name="originHost"
                  type="text"
                  required
                  pattern="[a-z0-9.-]+"
                  placeholder="example.com"
                  className="h-[44px] w-full rounded-[6px] border border-[#d6d3d0] bg-white px-[14px] text-[14px] text-[#23221e] placeholder:text-[#c1bdb7] focus:border-[#3b82f6] focus:outline-none focus:ring-2 focus:ring-[#3b82f6]/25"
                />
                <p className="mt-1 text-[12px] text-[#706d65]">
                  既存サイトのドメインを入れてください
                </p>
              </div>
              {createMutation.isError && (
                <div className="rounded-[6px] border-l-4 border-[#ef4444] bg-[#fef2f2] p-3 text-[13px] text-[#991b1b]">
                  作成エラー:{' '}
                  {formatErrorMessage(createMutation.error, 'サイトの作成に失敗しました')}
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowCreate(false)}
                  className="rounded-[6px] border border-[#d6d3d0] px-4 py-2 text-[13px] text-[#706d65] transition hover:bg-[#f8f7f6]"
                >
                  キャンセル
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="rounded-[6px] bg-[#3b82f6] px-4 py-2 text-[13px] font-medium text-white transition hover:bg-[#1d4ed8] disabled:opacity-50"
                >
                  {createMutation.isPending ? '作成中...' : '作成'}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Empty state */}
        {!isLoading && sites && sites.length === 0 && !showCreate && (
          <div className="mt-12 text-center">
            <p className="text-[14px] text-[#706d65]">
              まだサイトが登録されていません。「+ 新しいサイトを追加」からサイトを作成してください。
            </p>
          </div>
        )}
      </main>
    </div>
  );
}

export { LAST_SITE_ID_KEY };
